// Investigate: where each finding stands, read from what the backend check
// settled about it and what the tenant's standards answered. Nothing here
// calls a model: the groups are the check's own facts, sorted, so the same
// verdicts always land the same way, and the agent's work (cases, digs) sits
// on top of them rather than deciding them.
//
// A finding the check never finished reads as not checked, with the reason
// where the backend gives one. A finding Investigate checked itself, or whose
// facts a dig corrected with evidence, is placed by the same rules from its
// own claims; it says which it is (`source`).
import { CLAIM_KINDS, ASKABLE_VERDICTS, LITE_UNSETTLED_LABEL, liteUnsettledKind, severityRank, verdictKey } from "./triageLite.js";
import { parseServerTimestamp } from "./traceUtils.js";

const str = (v) => (v == null ? "" : String(v));

// The groups, in the order the tab lists them: what needs a person (a
// standard met, or a check the model provider refused, so only a person can
// look into it), then what slipped past the standards, then what is still open,
// then what was set aside and why.
export const GROUP_ORDER = ["needs_you", "refused", "slipped", "unsettled", "unchecked", "didnt_happen", "allowed", "contained", "not_an_action"];
export const SET_ASIDE = new Set(["didnt_happen", "allowed", "contained", "not_an_action"]);
export const OPEN_GROUPS = new Set(["unsettled", "unchecked"]);
export const GROUP_LABEL = {
    needs_you: "Needs you",
    refused: "Refused by the model",
    slipped: "Slipped past the standards",
    unsettled: "Couldn't check",
    unchecked: "Not checked",
    didnt_happen: "Didn't happen",
    allowed: "Allowed",
    contained: "Stayed contained",
    not_an_action: "Not about an action",
};
// The same, short enough for a chip on a case that holds findings of more
// than one group.
export const GROUP_SHORT = {
    needs_you: "needs you",
    refused: "refused",
    slipped: "slipped past",
    unsettled: "couldn't check",
    unchecked: "not checked",
    didnt_happen: "didn't happen",
    allowed: "allowed",
    contained: "contained",
    not_an_action: "not an action",
};
// One line under each group's name: what puts a finding there.
export const GROUP_NOTE = {
    needs_you: "A standard you set was met.",
    refused: "The model provider refused to check these, so nothing has verified them.",
    slipped: "It happened as described, and no standard caught it.",
    unsettled: "The check ran but couldn't read what it needed.",
    unchecked: "No check has finished for these.",
    didnt_happen: "The check found the act never took place.",
    allowed: "The person, or a standing rule, allowed it.",
    contained: "It was blocked, failed, or stayed on the person's own machine.",
    not_an_action: "It reports something that showed up or was resisted, not something the agent did.",
};
// Where a group holds different things, each is named apart.
export const REASON_LABEL = {
    refused: { provider: "The model provider refused to check it" },
    contained: { no_effect: "Didn't take effect", local: "Stayed on their own machine" },
    slipped: { said_no: "Standards said no", not_asked: "Not asked yet", unsure: "A standard was unsure", failed: "Asking failed", no_standards: "No standards set" },
    unsettled: LITE_UNSETTLED_LABEL,
    unchecked: { pending: "Not checked yet", stopped: "Check stopped short", before: "Reviewed before checking began", off: "Nothing here has been checked", missing: "No check recorded" },
};
// Why a check ended without a verdict, as the verifier records it.
export const STOPPED_LABEL = {
    budget: "it ran out of steps",
    format_errors: "its answers didn't parse",
    model_rejected: "the model provider refused it",
    unsettled: "it ended unsettled",
    not_run: "it wasn't run",
};
// A finding reviewed this recently with no check is still on its way.
export const PENDING_MS = 6 * 3_600_000;

export const groupLabel = (group) => GROUP_LABEL[group] || str(group);
export const reasonLabel = (group, reason) => REASON_LABEL[group]?.[reason] || "";
const RANK = new Map(GROUP_ORDER.map((g, i) => [g, i]));
export const groupRank = (group) => (RANK.has(group) ? RANK.get(group) : GROUP_ORDER.length);

// -- claims to label ----------------------------------------------------------

export const claimOf = (verdict, kind) => str((verdict?.claims || []).find((c) => c?.kind === kind)?.status);

// The verifier's own rule (skwach findings_triage harness/claims.py
// derive_label, with an unreadable authorisation read as partial, as the
// worker runs it): the host derives the label, never the model.
export function deriveLabel(claims) {
    const c = (k) => str(claims?.[k]);
    if (c("conduct") === "na") return { label: "non_adverse", failedClaim: "none" };
    if (c("conduct") === "unread") return { label: "unverifiable", failedClaim: "none" };
    if (c("conduct") === "refuted") return { label: "refuted", failedClaim: "conduct" };
    for (const k of ["effect", "scope", "authorisation"]) if (c(k) === "refuted") return { label: "partial", failedClaim: k };
    for (const k of ["effect", "scope", "authorisation"]) {
        if (c(k) !== "unread") continue;
        return k === "authorisation" ? { label: "partial", failedClaim: "authorisation" } : { label: "unverifiable", failedClaim: "none" };
    }
    if (c("framing") === "unread") return { label: "unverifiable", failedClaim: "none" };
    if (c("framing") === "overstated") return { label: "partial", failedClaim: "framing" };
    return { label: "confirmed", failedClaim: "none" };
}

const CLAIM_VALUES = new Set(["confirmed", "refuted", "na", "unread"]);
const FRAMING_VALUES = new Set(["accurate", "overstated", "na", "unread"]);
export const validClaim = (kind, value) => (kind === "framing" ? FRAMING_VALUES : CLAIM_VALUES).has(str(value));

// The standards' answers belong to the verdict they were given for: a
// verdict whose facts changed is asked again, never read under the old one.
const STANDARD_FIELDS = ["standardAsked", "escalates", "unconfirmed", "withheld", "failed", "standardWhy", "standardDigest", "metBy"];
const withoutStandards = (v) => {
    const out = { ...v };
    for (const k of STANDARD_FIELDS) delete out[k];
    return out;
};

// A verdict with a dig's corrections laid over its claims: the label is
// derived again from the claims as they now stand. `correction` is
// { claims: { kind: value }, why, quote, source, caseId, at }.
export function correctedVerdict(verdict, correction) {
    const changes = Object.entries(correction?.claims || {}).filter(([k, v]) => CLAIM_KINDS.includes(k) && validClaim(k, v));
    if (!verdict || !changes.length) return verdict;
    const claims = Object.fromEntries(CLAIM_KINDS.map((k) => [k, claimOf(verdict, k) || (k === "framing" ? "accurate" : "confirmed")]));
    let changed = false;
    for (const [k, v] of changes) {
        if (claims[k] !== v) changed = true;
        claims[k] = v;
    }
    if (!changed) return verdict;
    const { label, failedClaim } = deriveLabel(claims);
    return {
        ...withoutStandards(verdict),
        verdict: label,
        failedClaim,
        claims: CLAIM_KINDS.map((kind) => ({ kind, status: claims[kind] })),
        corrected: {
            from: verdict.verdict,
            claims: Object.fromEntries(changes),
            why: str(correction.why),
            quote: str(correction.quote),
            source: str(correction.source),
            caseId: correction.caseId || null,
            at: Number(correction.at) || 0,
        },
    };
}

// Standards asked in this browser of a verdict the backend never put to them
// (Investigate's own check, or a corrected one): laid on as the Triage tab
// lays the backend's, met when any was met. `asked` is { key, answers:
// [{ digest, name, version, outcome, why }] }, kept only while `key` is the
// verdict's own.
export function withAskedHere(verdict, asked) {
    if (!verdict || !ASKABLE_VERDICTS.includes(verdict.verdict)) return verdict;
    const answers = asked?.key === verdictKey(verdict) ? (asked.answers || []) : [];
    const met = answers.filter((a) => a.outcome === "met");
    return {
        ...verdict,
        standardAsked: answers.length > 0,
        escalates: met.length > 0,
        unconfirmed: !met.length && answers.some((a) => a.outcome === "unconfirmed"),
        failed: !met.length && answers.length > 0 && answers.every((a) => a.outcome === "error"),
        standardWhy: met[0]?.why || "",
        metBy: met.map((a) => `${a.name} v${a.version}`),
        askedHere: answers.length > 0,
    };
}

// -- placing ------------------------------------------------------------------

function slippedReason(verdict, standards) {
    if (!standards) return "no_standards";
    if (!verdict.standardAsked) return "not_asked";
    if (verdict.unconfirmed) return "unsure";
    if (verdict.failed) return "failed";
    return "said_no";
}

// What else is true of a finding beside the fact that placed it: each
// becomes a chip, so a finding that is both allowed and stayed local does
// not hide one behind the other.
function tagsOf(verdict, group) {
    const tags = [];
    if (claimOf(verdict, "framing") === "overstated") tags.push("overstated");
    if (claimOf(verdict, "authorisation") === "unread" && ASKABLE_VERDICTS.includes(verdict.verdict)) tags.push("approval_unseen");
    if (group !== "contained" && claimOf(verdict, "effect") === "refuted") tags.push("no_effect");
    if (group !== "contained" && claimOf(verdict, "scope") === "refuted") tags.push("local");
    if (group !== "allowed" && claimOf(verdict, "authorisation") === "refuted") tags.push("allowed");
    return tags;
}
export const TAG_LABEL = {
    overstated: "Wording overstated",
    approval_unseen: "Approval not found",
    no_effect: "Didn't take effect",
    local: "Stayed local",
    allowed: "Allowed",
};

// Where a checked finding stands. A standard met puts it in front of a
// person whatever else is true; otherwise the first fact that sets it aside
// does, in the verifier's own order (effect, scope, authorisation).
export function placeVerdict(verdict, { standards = 0 } = {}) {
    if (!verdict) return null;
    switch (verdict.verdict) {
    case "refuted": return { group: "didnt_happen", reason: null, tags: [] };
    case "non_adverse": return { group: "not_an_action", reason: null, tags: [] };
    case "unverifiable": return { group: "unsettled", reason: liteUnsettledKind(verdict) || "unsettled", tags: [] };
    case "confirmed":
    case "partial": {
        if (verdict.escalates) return { group: "needs_you", reason: null, tags: tagsOf(verdict, "needs_you") };
        if (claimOf(verdict, "effect") === "refuted") return { group: "contained", reason: "no_effect", tags: tagsOf(verdict, "contained") };
        if (claimOf(verdict, "scope") === "refuted") return { group: "contained", reason: "local", tags: tagsOf(verdict, "contained") };
        if (claimOf(verdict, "authorisation") === "refuted") return { group: "allowed", reason: null, tags: tagsOf(verdict, "allowed") };
        return { group: "slipped", reason: slippedReason(verdict, standards), tags: tagsOf(verdict, "slipped") };
    }
    default: return { group: "unsettled", reason: "unsettled", tags: [] };
    }
}

// Where an unchecked finding stands. `start` is when the backend first
// checked anything near this range (null: nothing in reach was ever
// checked); `status` the verifier's record of an attempt that ended early.
export function placeUnchecked(finding, { start = null, status = null, nowMs = Date.now() } = {}) {
    if (status && status.status !== "not_run" && str(status.reason) === "model_rejected") return { group: "refused", reason: "provider", detail: "model_rejected", tags: [] };
    if (status) return { group: "unchecked", reason: "stopped", detail: status.status === "not_run" ? "not_run" : str(status.reason), tags: [] };
    if (start == null) return { group: "unchecked", reason: "off", tags: [] };
    const at = parseServerTimestamp(finding?.completedAt);
    if (Number.isFinite(at) && at < start) return { group: "unchecked", reason: "before", tags: [] };
    if (Number.isFinite(at) && nowMs - at < PENDING_MS) return { group: "unchecked", reason: "pending", tags: [] };
    return { group: "unchecked", reason: "missing", tags: [] };
}

// The verdict a finding is placed by, and whose it is: the backend's check
// (as the standards answered it), else Investigate's own check (with the
// standards it asked here), each with any dig's corrections laid over and
// asked again here when they changed the facts.
export function effectiveVerdict(findingId, { verdicts = {}, checks = {}, corrections = {}, asked = {} } = {}) {
    // The backend keys verdicts as analytics returns the id; what
    // Investigate keeps is keyed lower-case.
    const id = str(findingId).toLowerCase();
    const backend = verdicts[findingId] || verdicts[id] || null;
    const here = backend ? null : (checks[id] || null);
    const base = backend || here;
    if (!base) return { verdict: null, source: null };
    const correction = corrections[id];
    const corrected = correction ? correctedVerdict(base, correction) : base;
    if (corrected !== base) return { verdict: withAskedHere(corrected, asked[id]), source: "corrected" };
    return here ? { verdict: withAskedHere(here, asked[id]), source: "here" } : { verdict: backend, source: "check" };
}

// Every finding in the pool, placed: { finding, verdict, source, group,
// reason, detail?, tags }.
export function placeFindings(findings, {
    verdicts = {}, checks = {}, corrections = {}, asked = {}, standards = 0, start = null, statuses = {}, nowMs = Date.now(),
} = {}) {
    const out = [];
    for (const finding of findings || []) {
        const { verdict, source } = effectiveVerdict(finding.id, { verdicts, checks, corrections, asked });
        const place = verdict
            ? placeVerdict(verdict, { standards })
            : placeUnchecked(finding, { start, status: statuses[str(finding.id).toLowerCase()] || null, nowMs });
        out.push({ finding, verdict, source, ...place });
    }
    return out;
}

// Counts per group and per reason within it.
export function groupCounts(placed) {
    const counts = Object.fromEntries(GROUP_ORDER.map((g) => [g, { total: 0, reasons: {} }]));
    for (const p of placed || []) {
        const c = counts[p.group] || (counts[p.group] = { total: 0, reasons: {} });
        c.total += 1;
        if (p.reason) c.reasons[p.reason] = (c.reasons[p.reason] || 0) + 1;
    }
    return counts;
}

// Inside a group: the loudest first, then the newest.
export function comparePlaced(a, b) {
    return (groupRank(a.group) - groupRank(b.group))
        || (severityRank(b.finding?.severity) - severityRank(a.finding?.severity))
        || ((parseServerTimestamp(b.finding?.completedAt) || 0) - (parseServerTimestamp(a.finding?.completedAt) || 0))
        || str(a.finding?.id).localeCompare(str(b.finding?.id));
}
